// One angular convention for every light that orbits an aim point — the shadow
// sun, the live key light, the torch. Lifted out of Scene.jsx when the torch
// arrived: the placement is now also read by the gizmo adapter and a second copy
// would be two conventions that silently disagree about which way azimuth 90 is.

/**
 * Spherical (distance, elevation deg, azimuth deg around Y from +Z) → world XYZ.
 */
export function sphericalToXYZ(dist, elevDeg, azimDeg, origin) {
  const elev = (elevDeg * Math.PI) / 180
  const azim = (azimDeg * Math.PI) / 180
  const horizontal = dist * Math.cos(elev)
  return [
    origin[0] + horizontal * Math.sin(azim),
    origin[1] + dist * Math.sin(elev),
    origin[2] + horizontal * Math.cos(azim),
  ]
}

// Inverse of sphericalToXYZ — a world position back into the same three knobs,
// so a light dragged in the viewport lands in the store as the values its
// sliders show, rather than as a second, competing representation.
export function xyzToSpherical(pos, origin) {
  const dx = pos[0] - origin[0]
  const dy = pos[1] - origin[1]
  const dz = pos[2] - origin[2]
  const distance = Math.hypot(dx, dy, dz)
  if (distance < 1e-9) return { distance: 0, elevation: 0, azimuth: 0 }
  return {
    distance,
    elevation: (Math.asin(Math.max(-1, Math.min(1, dy / distance))) * 180) / Math.PI,
    azimuth: (Math.atan2(dx, dz) * 180) / Math.PI,
  }
}

// Where a light "placed at the camera" actually has to go: on the view axis,
// but IN FRONT of the near plane. At the camera exactly it renders inside the
// near plane and is invisible — the same reason the headlamp hides its emitter
// cube — so walk it a fraction of the way toward whatever the camera is aiming
// at. `minAhead` wins over the fraction when the camera is already nose-to-nose
// with its target: a light you can't see is worse than one slightly past its
// aim point. No view axis to advance along (camera sitting on the target) →
// the camera position itself, not a NaN.
export function placeAtView(camPos, aimPos, { fraction = 0.4, minAhead = 0.15 } = {}) {
  const dx = aimPos[0] - camPos[0]
  const dy = aimPos[1] - camPos[1]
  const dz = aimPos[2] - camPos[2]
  const len = Math.hypot(dx, dy, dz)
  if (!(len > 1e-9)) return [camPos[0], camPos[1], camPos[2]]
  const k = Math.max(minAhead, len * fraction) / len
  return [camPos[0] + dx * k, camPos[1] + dy * k, camPos[2] + dz * k]
}
